import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  ActionBar,
  Button,
  Field,
  FormCard,
  SectionHeader,
  SelectField,
  type Column,
} from "@fawkes/ui";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-pension: "Hoogseizoen dagen" — branch-scoped CRUD over DagPensionVol (a full-boarding
// date per animal type, for one vestiging). React port of the Jmix DagPensionVol list/detail views.
// The list/create are scoped to the selected branch via ?vestigingId=; the backend validates access.

interface DagPensionVol {
  id: number;
  datum: string;
  diersoortId: number;
  diersoortNaam: string | null;
  vestigingId: number;
  vestigingCode: string;
}

interface Diersoort {
  id: number;
  naam: string;
}

interface Draft {
  datum: string;
  diersoortId: string;
}

const EMPTY_DRAFT: Draft = { datum: "", diersoortId: "" };

export function DagPensionVolList({ vestigingId, onBack }: { vestigingId: number; onBack: () => void }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<DagPensionVol[]>({
    url: `pension/hoogseizoen-dagen?vestigingId=${vestigingId}`,
    method: "get",
  });
  const { data: diersoortenData } = useCustom<Diersoort[]>({
    url: "pension/diersoorten",
    method: "get",
  });
  const { mutate, isLoading: isSaving } = useCustomMutation();

  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  const rows = data?.data ?? [];
  const diersoorten = diersoortenData?.data ?? [];

  function startCreate() {
    setDraft(EMPTY_DRAFT);
    setEditingId("new");
  }

  function startEdit(row: DagPensionVol) {
    setDraft({ datum: row.datum, diersoortId: String(row.diersoortId) });
    setEditingId(row.id);
  }

  function cancel() {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  }

  function save() {
    const isCreate = editingId === "new";
    const values = isCreate
      ? { datum: draft.datum, diersoortId: Number(draft.diersoortId), vestigingId }
      : { datum: draft.datum, diersoortId: Number(draft.diersoortId) };
    mutate(
      {
        url: isCreate ? "pension/hoogseizoen-dagen" : `pension/hoogseizoen-dagen/${editingId}`,
        method: isCreate ? "post" : "put",
        values,
      },
      {
        onSuccess: () => {
          cancel();
          refetch();
        },
      },
    );
  }

  function remove(row: DagPensionVol) {
    mutate(
      { url: `pension/hoogseizoen-dagen/${row.id}`, method: "delete", values: {} },
      { onSuccess: () => refetch() },
    );
  }

  const saveDisabled = isSaving || draft.datum.trim() === "" || draft.diersoortId.trim() === "";

  const columns: Column<DagPensionVol>[] = [
    { header: t("instellingen.pension.hoogseizoen.col.datum"), cell: (r) => r.datum },
    {
      header: t("instellingen.pension.hoogseizoen.col.diersoort"),
      cell: (r) => r.diersoortNaam ?? `#${r.diersoortId}`,
    },
    {
      header: "",
      cell: (r) => (
        <span className="pension-row-actions">
          <Button variant="secondary" onClick={() => startEdit(r)}>
            {t("instellingen.pension.edit")}
          </Button>
          <Button variant="secondary" onClick={() => remove(r)} disabled={isSaving}>
            {t("instellingen.pension.delete")}
          </Button>
        </span>
      ),
    },
  ];

  const diersoortOptions = [
    { value: "", label: t("instellingen.pension.diersoort.placeholder") },
    ...diersoorten.map((d) => ({ value: String(d.id), label: d.naam })),
  ];

  return (
    <FormCard>
      <SectionHeader>{t("instellingen.item.pensionVol")}</SectionHeader>
      <p className="fawkes-muted">{t("instellingen.pension.pensionVol.desc")}</p>

      {editingId !== null ? (
        <div className="fawkes-form-grid">
          <Field
            label={t("instellingen.pension.field.datum")}
            type="date"
            value={draft.datum}
            onChange={(e) => setDraft({ ...draft, datum: e.target.value })}
          />
          <SelectField
            label={t("instellingen.pension.field.diersoort")}
            value={draft.diersoortId}
            options={diersoortOptions}
            onChange={(e) => setDraft({ ...draft, diersoortId: e.target.value })}
          />
          <ActionBar>
            <Button variant="primary" onClick={save} disabled={saveDisabled}>
              {t("common.save")}
            </Button>
            <Button variant="secondary" onClick={cancel} disabled={isSaving}>
              {t("common.cancel")}
            </Button>
          </ActionBar>
        </div>
      ) : (
        <div className="fawkes-list-toolbar">
          <Button variant="primary" onClick={startCreate}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("instellingen.pension.new")}
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("instellingen.pension.hoogseizoen.loadFailed")}</p>
      ) : (
        <IndeelbareTabel tabelKey="settings-dag-pension-vol-list"
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.id)}
          empty={t("instellingen.pension.hoogseizoen.empty")}
        />
      )}

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
